import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '../lib/supabase';

interface Auction {
  id: string;
  title: string;
  price: string;
  location: string;
  type: string;
  source: 'caixa' | 'superbid' | 'zuk';
  link: string;
  updatedAt: string;
}

interface AuctionState {
  auctions: Auction[];
  lastUpdate: string | null;
  isLoading: boolean;
  error: string | null;
  selectedSource: 'all' | 'caixa' | 'superbid' | 'zuk';
  setSelectedSource: (source: 'all' | 'caixa' | 'superbid' | 'zuk') => void;
  fetchAuctions: () => Promise<void>;
  filteredAuctions: () => Auction[];
}

export const useAuctionStore = create<AuctionState>()(
  persist(
    (set, get) => ({
      auctions: [],
      lastUpdate: null,
      isLoading: false,
      error: null,
      selectedSource: 'all',
      setSelectedSource: (source) => set({ selectedSource: source }),
      fetchAuctions: async () => {
        try {
          set({ isLoading: true, error: null });

          // Simulated data for demonstration
          const mockAuctions = [
            {
              id: '1',
              title: 'Apartamento 2 Quartos - Centro',
              price: 'R$ 150.000,00',
              location: 'São Paulo, SP',
              type: 'Apartamento',
              source: 'caixa',
              link: 'https://venda-imoveis.caixa.gov.br/sistema/detalhe-imovel.asp?hdnOrigem=index&hdnRef=1',
              updatedAt: new Date().toISOString()
            },
            {
              id: '2',
              title: 'Casa 3 Quartos com Quintal',
              price: 'R$ 280.000,00',
              location: 'Rio de Janeiro, RJ',
              type: 'Casa',
              source: 'caixa',
              link: 'https://venda-imoveis.caixa.gov.br/sistema/detalhe-imovel.asp?hdnOrigem=index&hdnRef=2',
              updatedAt: new Date().toISOString()
            },
            {
              id: '3',
              title: 'Sala Comercial 45m²',
              price: 'R$ 120.000,00',
              location: 'Campinas, SP',
              type: 'Comercial',
              source: 'superbid',
              link: 'https://www.superbid.net/imovel/3',
              updatedAt: new Date().toISOString()
            }
          ];

          set({
            auctions: mockAuctions,
            lastUpdate: new Date().toISOString(),
            isLoading: false,
            error: null
          });
        } catch (error) {
          console.error('Error fetching auctions:', error);
          set({
            error: 'Erro ao carregar leilões. Por favor, tente novamente.',
            isLoading: false
          });
        }
      },
      filteredAuctions: () => {
        const state = get();
        if (state.selectedSource === 'all') {
          return state.auctions;
        }
        return state.auctions.filter(auction => auction.source === state.selectedSource);
      }
    }),
    {
      name: 'auction-storage',
      partialize: (state) => ({
        auctions: state.auctions,
        lastUpdate: state.lastUpdate,
        selectedSource: state.selectedSource
      })
    }
  )
);